import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import inductionService from '../../services/induction';
import { errorMessage } from '../../utils/apiResponse';
import type { CertificationTypeOption, EquipmentInductionDetail, Hazard, InductionInput } from '../../types/induction';

interface Props {
  induction: EquipmentInductionDetail;
  onClose: () => void;
  onSaved: (updated: EquipmentInductionDetail) => void;
}

const clean = (list: string[]) => list.map(s => s.trim()).filter(Boolean);

/** Edit an equipment induction's content and rules. */
export default function InductionEditForm({ induction, onClose, onSaved }: Props) {
  const [title, setTitle] = useState(induction.title);
  const [summary, setSummary] = useState(induction.summary ?? '');
  const [hazards, setHazards] = useState<Hazard[]>(induction.hazards?.length ? induction.hazards : []);
  const [ppe, setPpe] = useState<string[]>(induction.ppe ?? []);
  const [rules, setRules] = useState<string[]>(induction.operating_rules ?? []);
  const [checks, setChecks] = useState<string[]>(induction.practical_checks ?? []);
  const [emergency, setEmergency] = useState(induction.emergency_procedure ?? '');
  const [licenceNote, setLicenceNote] = useState(induction.licence_note ?? '');
  const [manualUrl, setManualUrl] = useState(induction.manual_url ?? '');
  const [refresher, setRefresher] = useState(induction.refresher_months ? String(induction.refresher_months) : '');
  const [required, setRequired] = useState(induction.is_required);
  const [active, setActive] = useState(induction.active);
  const [certIds, setCertIds] = useState<number[]>(induction.required_certification_type_ids ?? []);
  const [certBlock, setCertBlock] = useState(induction.certificates_block);
  const [material, setMaterial] = useState(false);
  const [certTypes, setCertTypes] = useState<CertificationTypeOption[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    inductionService.certificationTypes().then(setCertTypes).catch(() => setCertTypes([]));
  }, []);

  const submit = async () => {
    if (!title.trim()) { setError('Give the induction a title.'); return; }
    const months = refresher.trim() ? parseInt(refresher, 10) : null;
    if (months !== null && (!Number.isInteger(months) || months < 1 || months > 120)) {
      setError('Refresher months must be between 1 and 120, or blank.');
      return;
    }
    const input: InductionInput = {
      title: title.trim(),
      summary: summary.trim() || null,
      hazards: hazards.filter(h => h.hazard.trim()).map(h => ({ hazard: h.hazard.trim(), control: h.control?.trim() || null })),
      ppe: clean(ppe),
      operating_rules: clean(rules),
      practical_checks: clean(checks),
      emergency_procedure: emergency.trim() || null,
      licence_note: licenceNote.trim() || null,
      manual_url: manualUrl.trim() || null,
      refresher_months: months,
      is_required: required,
      active,
      required_certification_type_ids: certIds,
      certificates_block: certBlock,
      material_change: material,
    };
    setSaving(true);
    setError(null);
    try {
      onSaved(await inductionService.update(induction.id, input));
    } catch (e) {
      setError(errorMessage(e, 'Could not save the induction'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title="Edit induction" onClose={onClose} onSubmit={submit} submitting={saving} error={error}>
      <Field label="Title" htmlFor="ind-title">
        <input id="ind-title" className={inputClass} value={title} onChange={e => setTitle(e.target.value)} />
      </Field>
      <Field label="Summary" htmlFor="ind-summary">
        <textarea id="ind-summary" className={inputClass} rows={3} value={summary} onChange={e => setSummary(e.target.value)} />
      </Field>

      <fieldset>
        <legend className="block text-sm font-medium text-neutral-700 mb-1">Hazards and controls</legend>
        <div className="space-y-2">
          {hazards.map((h, i) => (
            <div key={i} className="rounded-lg border border-neutral-200 p-2 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  className={`${inputClass} min-w-0`}
                  placeholder="Hazard"
                  aria-label={`Hazard ${i + 1}`}
                  value={h.hazard}
                  onChange={e => setHazards(hs => hs.map((x, j) => j === i ? { ...x, hazard: e.target.value } : x))}
                />
                <button type="button" className="shrink-0 p-1 text-neutral-400 hover:text-error-600" aria-label={`Remove hazard ${i + 1}`}
                  onClick={() => setHazards(hs => hs.filter((_, j) => j !== i))}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <textarea
                className={inputClass}
                rows={2}
                placeholder="What controls it"
                aria-label={`Control for hazard ${i + 1}`}
                value={h.control ?? ''}
                onChange={e => setHazards(hs => hs.map((x, j) => j === i ? { ...x, control: e.target.value } : x))}
              />
            </div>
          ))}
          <AddButton onClick={() => setHazards(hs => [...hs, { hazard: '', control: '' }])}>Add hazard</AddButton>
        </div>
      </fieldset>

      <ListEditor legend="What to wear" itemLabel="PPE item" items={ppe} onChange={setPpe} addLabel="Add PPE" />
      <ListEditor legend="Rules" itemLabel="Rule" items={rules} onChange={setRules} addLabel="Add rule" multiline />
      <ListEditor legend="What the assessor watches for" itemLabel="Practical check" items={checks} onChange={setChecks} addLabel="Add check" />

      <Field label="In an emergency" htmlFor="ind-emergency">
        <textarea id="ind-emergency" className={inputClass} rows={3} value={emergency} onChange={e => setEmergency(e.target.value)} />
      </Field>
      <Field label="Licence note" htmlFor="ind-licence" hint="Shown to the operator, e.g. “A forklift licence (LF) is required.”">
        <input id="ind-licence" className={inputClass} value={licenceNote} onChange={e => setLicenceNote(e.target.value)} />
      </Field>
      <Field label="Operator manual link" htmlFor="ind-manual">
        <input id="ind-manual" type="url" className={inputClass} value={manualUrl} onChange={e => setManualUrl(e.target.value)} placeholder="https://" />
      </Field>
      <Field label="Refresher every (months)" htmlFor="ind-refresher" hint="Blank means a sign-off does not expire.">
        <input id="ind-refresher" type="number" min={1} max={120} className={inputClass} value={refresher} onChange={e => setRefresher(e.target.value)} />
      </Field>

      <fieldset>
        <legend className="block text-sm font-medium text-neutral-700 mb-1">Required certifications</legend>
        {certTypes === null && <p className="text-sm text-neutral-500">Loading…</p>}
        {certTypes !== null && certTypes.length === 0 && (
          <p className="text-sm text-neutral-500 break-words">No certification types yet — add them on Staff Certifications.</p>
        )}
        {certTypes && certTypes.length > 0 && (
          <div className="max-h-40 overflow-y-auto rounded-lg border border-neutral-200 divide-y divide-neutral-100">
            {certTypes.map(c => (
              <label key={c.id} className="flex items-start gap-2 px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0"
                  checked={certIds.includes(c.id)}
                  onChange={() => setCertIds(ids => ids.includes(c.id) ? ids.filter(x => x !== c.id) : [...ids, c.id])}
                />
                <span className="min-w-0 break-words">{c.name}{c.category && <span className="text-neutral-400"> · {c.category}</span>}</span>
              </label>
            ))}
          </div>
        )}
        <Check checked={certBlock} onChange={setCertBlock} className="mt-2">
          Refuse anyone without them — a missing certification cannot be overridden
        </Check>
      </fieldset>

      <div className="space-y-2">
        <Check checked={required} onChange={setRequired}>Required — nobody operates these machines without it</Check>
        <Check checked={active} onChange={setActive}>Active</Check>
        <Check checked={material} onChange={setMaterial}>
          This is a material change — ask everyone signed off to re-acknowledge it
        </Check>
      </div>
    </FormModal>
  );
}

function Check({ checked, onChange, className, children }: { checked: boolean; onChange: (v: boolean) => void; className?: string; children: React.ReactNode }) {
  return (
    <label className={`flex items-start gap-2 text-sm text-neutral-700 ${className ?? ''}`}>
      <input type="checkbox" className="mt-0.5 shrink-0" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="min-w-0 break-words">{children}</span>
    </label>
  );
}

function AddButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline">
      <Plus className="h-4 w-4" /> {children}
    </button>
  );
}

function ListEditor({ legend, itemLabel, items, onChange, addLabel, multiline }: {
  legend: string; itemLabel: string; items: string[]; onChange: (v: string[]) => void; addLabel: string; multiline?: boolean;
}) {
  const set = (i: number, v: string) => onChange(items.map((x, j) => j === i ? v : x));
  return (
    <fieldset>
      <legend className="block text-sm font-medium text-neutral-700 mb-1">{legend}</legend>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            {multiline
              ? <textarea className={`${inputClass} min-w-0`} rows={2} aria-label={`${itemLabel} ${i + 1}`} value={item} onChange={e => set(i, e.target.value)} />
              : <input className={`${inputClass} min-w-0`} aria-label={`${itemLabel} ${i + 1}`} value={item} onChange={e => set(i, e.target.value)} />}
            <button type="button" className="shrink-0 p-2 text-neutral-400 hover:text-error-600" aria-label={`Remove ${itemLabel.toLowerCase()} ${i + 1}`}
              onClick={() => onChange(items.filter((_, j) => j !== i))}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <AddButton onClick={() => onChange([...items, ''])}>{addLabel}</AddButton>
      </div>
    </fieldset>
  );
}
